import React, { useState } from 'react';
import { Calendar, Plus, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExerciceSelector({ 
  exercices, 
  selectedExercice, 
  onSelect, 
  buildingId, 
  token,
  onExerciceCreated 
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newExercice, setNewExercice] = useState({
    annee: new Date().getFullYear(),
    date_debut: `${new Date().getFullYear()}-01-01`,
    date_fin: `${new Date().getFullYear()}-12-31`,
    plan_comptable: 'simplifie'
  });

  const handleCreate = async () => {
    if (!buildingId) {
      toast.error('Sélectionnez un immeuble');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/exercices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          building_id: buildingId,
          nom: `Exercice ${newExercice.annee}`,
          date_debut: newExercice.date_debut,
          date_fin: newExercice.date_fin,
          plan_comptable: newExercice.plan_comptable
        })
      });

      if (response.ok) {
        toast.success('Exercice créé avec succès');
        setShowCreateModal(false);
        onExerciceCreated?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white"
      >
        <Calendar className="h-4 w-4 text-gray-500" />
        <span className="font-medium">
          {selectedExercice?.nom || 'Sélectionner un exercice'}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 z-50">
          <div className="p-2">
            {exercices.length === 0 ? (
              <p className="text-sm text-gray-500 p-2">Aucun exercice</p>
            ) : (
              exercices.map(ex => (
                <button
                  key={ex.id}
                  onClick={() => {
                    onSelect(ex);
                    setShowDropdown(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-gray-50 ${
                    selectedExercice?.id === ex.id ? 'bg-[#00D3A8]/10 text-[#00D3A8]' : ''
                  }`}
                >
                  <div className="font-medium">{ex.nom}</div>
                  <div className="text-xs text-gray-400">
                    {new Date(ex.date_debut).toLocaleDateString('fr-FR')} - {new Date(ex.date_fin).toLocaleDateString('fr-FR')}
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="border-t border-gray-100 p-2">
            <button
              onClick={() => {
                setShowDropdown(false);
                setShowCreateModal(true);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded-lg"
            >
              <Plus className="h-4 w-4" />
              Nouvel exercice
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold">Nouvel exercice comptable</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
                <input
                  type="number"
                  value={newExercice.annee}
                  onChange={(e) => {
                    const year = parseInt(e.target.value);
                    setNewExercice({
                      ...newExercice,
                      annee: year,
                      date_debut: `${year}-01-01`,
                      date_fin: `${year}-12-31`
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
                  <input
                    type="date"
                    value={newExercice.date_debut}
                    onChange={(e) => setNewExercice({ ...newExercice, date_debut: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                  <input
                    type="date"
                    value={newExercice.date_fin}
                    onChange={(e) => setNewExercice({ ...newExercice, date_fin: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan comptable</label>
                <select
                  value={newExercice.plan_comptable}
                  onChange={(e) => setNewExercice({ ...newExercice, plan_comptable: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="simplifie">Simplifié (petites copros)</option>
                  <option value="standard">Standard (décret 2005)</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] disabled:opacity-50"
              >
                {creating ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
